import { useEffect, useRef, type DependencyList } from "react";
import * as Option from "@utils/Option";

import type { IOts, OptionTs } from "@ps-bridge/interop";
import type { EmitOnInit, EmitOnRouteChange } from "@ps-bridge/capabilitiesTypes";
import { ok, type Ok } from "@ps-bridge/ok";
import { runIO } from "@view/effects/effectRunner";

export const useIoEffect = <Value>(io: IOts<Value>, deps: DependencyList): Ok => {
    const resolvedDeps = deps;
    useEffect(() => {
        runIO(io);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- deps are caller-provided and explicit by design.
    }, resolvedDeps);
    return ok;
};

export const useIoEffectOption = <Value>(
    io: OptionTs<IOts<Value>>,
    deps: DependencyList,
): Ok => {
    const resolvedDeps = deps;
    useEffect(() => {
        Option.fold(io, () => undefined, (ioValue: IOts<Value>) => {
                runIO(ioValue);
            },);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- deps are caller-provided and explicit by design.
    }, resolvedDeps);
    return ok;
};

export const useRouteEffect = <Route, Value>(
    route: OptionTs<Route>,
    io: IOts<Value>,
): Ok => useIoEffect(io, [route]);

export const useOnRouteChange = <Route>(
    route: OptionTs<Route>,
    emit: EmitOnRouteChange<OptionTs<Route>>,
): Ok => useRouteEffect(route, emit(route));

export const useOnInit = (init: OptionTs<EmitOnInit>, deps?: DependencyList): Ok => {
    const resolvedDeps = deps ?? [init];
    const hasRunRef = useRef(false);
    useEffect(() => {
        if (hasRunRef.current) return;
        Option.fold(
            init,
            () => undefined,
            (initValue: EmitOnInit) => {
                runIO(initValue);
                hasRunRef.current = true;
            },
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps -- deps are caller-provided; init is included in the fallback.
    }, resolvedDeps);
    return ok;
};

export const useOnInitWithReady = (
    init: OptionTs<EmitOnInit>,
    onReady: () => Ok,
    deps?: DependencyList,
): Ok => {
    const resolvedDeps = deps ?? [init];
    const hasRunRef = useRef(false);
    useEffect(() => {
        if (hasRunRef.current) return;
        Option.fold(
            init,
            () => {
                onReady();
                hasRunRef.current = true;
            },
            (initValue: EmitOnInit) => {
                runIO(initValue);
                onReady();
                hasRunRef.current = true;
            },
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps -- deps are caller-provided; init is included in the fallback.
    }, resolvedDeps);
    return ok;
};
